import type { AppConfig } from "../config";
import type { FreshTokens } from "./auth";

/**
 * Cognito's user-facing JSON API, called by the browser with the user's own
 * access token (section 13.3). No SDK: each call is one POST to the service
 * root with an X-Amz-Target header and no signature - these operations take
 * the token in the body. Nothing here needs, or has, a server privilege.
 * Learn's `cognito-api.ts` (gairm/lms 280619c), verbatim but for the config type, plus GlobalSignOut (Account ›
 * Security's Sign out everywhere).
 */

export class CognitoApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface PasskeyRecord {
  CredentialId: string;
  FriendlyCredentialName?: string;
  AuthenticatorAttachment?: string;
  AuthenticatorTransports?: string[];
  CreatedAt?: number;
  RelyingPartyId?: string;
}

/** WebAuthn: a credential answers only on its relying party or a subdomain of it (pitfall 17). */
export function usableHere(c: Pick<PasskeyRecord, "RelyingPartyId">, hostname = typeof window === "undefined" ? "" : window.location.hostname): boolean {
  const rp = c.RelyingPartyId?.toLowerCase();
  if (!rp) return true;
  const host = hostname.toLowerCase();
  return host === rp || host.endsWith(`.${rp}`);
}

/** WebAuthn is available here: what the passkey card, the step-up dialog and the passkey nudge all check. */
export function passkeysPossible(): boolean {
  return typeof navigator !== "undefined" && "credentials" in navigator && typeof PublicKeyCredential !== "undefined";
}

export function cognitoServiceRoot(config: AppConfig): string {
  return `${new URL(config.cognito.issuer).origin}/`;
}

export function cognitoApi(config: AppConfig) {
  const root = cognitoServiceRoot(config);
  const clientId = config.cognito.clientId;
  // `timeoutMs`: give up after that long with a `TimeoutError` (a renewal must not hang the page that waits on it), and
  // a body that is not JSON (an edge or gateway page) is reported by its status, not as a parse error. Ported from
  // txp-labs/mermera-attestation-app apps/web/src/auth/cognito-api.ts @ 4fb8cec (gairm/lms#391).
  const call = async <T>(target: string, body: Record<string, unknown>, timeoutMs?: number): Promise<T> => {
    const abort = timeoutMs ? new AbortController() : undefined;
    const timer = abort ? setTimeout(() => abort.abort(), timeoutMs) : undefined;
    let res: Response;
    let text: string;
    try {
      res = await fetch(root, {
        method: "POST",
        headers: { "content-type": "application/x-amz-json-1.1", "x-amz-target": `AWSCognitoIdentityProviderService.${target}` },
        body: JSON.stringify(body),
        ...(abort ? { signal: abort.signal } : {}),
      });
      text = await res.text();
    } catch (err) {
      if (abort?.signal.aborted) throw new CognitoApiError("TimeoutError", `${target} took longer than ${timeoutMs} ms`);
      throw err;
    } finally {
      clearTimeout(timer);
    }
    let json: Record<string, unknown> & { __type?: string; message?: string } = {};
    try {
      json = text ? (JSON.parse(text) as typeof json) : {};
    } catch {
      // Not JSON: the status says enough.
    }
    if (!res.ok) throw new CognitoApiError(String(json.__type ?? "CognitoError").split("#").pop() ?? "CognitoError", String(json.message ?? `Cognito answered ${res.status}`));
    return json as T;
  };

  return {
    listPasskeys: (accessToken: string) => call<{ Credentials?: PasskeyRecord[] }>("ListWebAuthnCredentials", { AccessToken: accessToken, MaxResults: 20 }),
    startPasskey: (accessToken: string) => call<{ CredentialCreationOptions: unknown }>("StartWebAuthnRegistration", { AccessToken: accessToken }),
    completePasskey: (accessToken: string, credential: unknown) => call<Record<string, never>>("CompleteWebAuthnRegistration", { AccessToken: accessToken, Credential: credential }),
    deletePasskey: (accessToken: string, credentialId: string) => call<Record<string, never>>("DeleteWebAuthnCredential", { AccessToken: accessToken, CredentialId: credentialId }),

    changePassword: (accessToken: string, previous: string, proposed: string) =>
      call<Record<string, never>>("ChangePassword", { AccessToken: accessToken, PreviousPassword: previous, ProposedPassword: proposed }),
    associateSoftwareToken: (accessToken: string) => call<{ SecretCode?: string }>("AssociateSoftwareToken", { AccessToken: accessToken }),
    verifySoftwareToken: (accessToken: string, code: string, deviceName: string) =>
      call<{ Status?: string }>("VerifySoftwareToken", { AccessToken: accessToken, UserCode: code, FriendlyDeviceName: deviceName }),
    /**
     * First sign-in's authenticator (gairm/lms#393): the same two calls with the `MFA_SETUP` challenge's session in place
     * of an access token, which the person does not have yet. Each answers a new session; the last one answers the
     * challenge. Measured on lms-dev, 2026-10-01: a wrong code is `EnableSoftwareTokenMFAException` and leaves the
     * association's session usable for the right one.
     */
    associateSoftwareTokenInSignIn: (session: string) => call<{ SecretCode?: string; Session?: string }>("AssociateSoftwareToken", { Session: session }),
    verifySoftwareTokenInSignIn: (session: string, code: string, deviceName: string) =>
      call<{ Status?: string; Session?: string }>("VerifySoftwareToken", { Session: session, UserCode: code, FriendlyDeviceName: deviceName }),
    setTotpPreference: (accessToken: string) =>
      call<Record<string, never>>("SetUserMFAPreference", { AccessToken: accessToken, SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true } }),
    globalSignOut: (accessToken: string) => call<Record<string, never>>("GlobalSignOut", { AccessToken: accessToken }),

    /**
     * The session layer's renewal (gairm/lms#391): new ID and access tokens from the refresh token, through the Cognito
     * API rather than managed login's /oauth2/token. No refresh token comes back while the client has no rotation. The
     * pool tracks devices, so a refresh token minted by an API sign-in renews only once its device is confirmed, and
     * only with that device's key (probed on lms-dev 2026-10-01: refused without the key, and refused unconfirmed with
     * it); a managed-login refresh token carries no device and renews without one.
     */
    getTokensFromRefreshToken: (refreshToken: string, deviceKey?: string, timeoutMs?: number) =>
      call<{ AuthenticationResult?: FreshTokens }>("GetTokensFromRefreshToken", { ClientId: clientId, RefreshToken: refreshToken, ...(deviceKey ? { DeviceKey: deviceKey } : {}) }, timeoutMs),

    /**
     * Sign-in and step-up (gairm/lms#392): the choice-based flow, identifier-first. With no `preferred`, Cognito answers
     * `SELECT_CHALLENGE` with the factors on offer; with `WEB_AUTHN` preferred it answers the passkey challenge at once
     * when the person has one, else `SELECT_CHALLENGE`; with `PASSWORD` and the password, the next step or the tokens.
     * `contextData` is the advanced-security data (R8; context-data.ts) — sent with every sign-in call when the browser
     * produced it. `deviceKey` is a remembered device's (gairm/lms#400): with it, a password is answered `DEVICE_SRP_AUTH`
     * in place of the authenticator code (proven on lms-dev under USER_AUTH, 2026-10-02), and a key Cognito no longer
     * knows is refused `ResourceNotFoundException` ("Device does not exist.").
     * `clientMetadata` reaches the pre-token-generation trigger from `RespondToAuthChallenge` (R12; gairm/lms#421):
     * step-up sends `STEP_UP_METADATA`, so the trail's row says `session.stepped_up`.
     */
    startUserAuth: (p: { username: string; preferred?: "WEB_AUTHN" | "PASSWORD"; password?: string; deviceKey?: string; contextData?: UserContextData; clientMetadata?: Record<string, string> }) =>
      call<AuthAnswer>("InitiateAuth", {
        AuthFlow: "USER_AUTH",
        ClientId: clientId,
        AuthParameters: { USERNAME: p.username, ...(p.preferred ? { PREFERRED_CHALLENGE: p.preferred } : {}), ...(p.password ? { PASSWORD: p.password } : {}), ...(p.deviceKey ? { DEVICE_KEY: p.deviceKey } : {}) },
        ...(p.contextData ? { UserContextData: p.contextData } : {}),
        ...(p.clientMetadata ? { ClientMetadata: p.clientMetadata } : {}),
      }),
    /** `clientMetadata` as for `startUserAuth`: step-up's machine sends it on every call of its ceremony (gairm/lms#397). */
    respondToChallenge: (challengeName: string, session: string, responses: Record<string, string>, contextData?: UserContextData, clientMetadata?: Record<string, string>) =>
      call<AuthAnswer>("RespondToAuthChallenge", {
        ChallengeName: challengeName,
        ClientId: clientId,
        // DEVICE_SRP_AUTH is answered with no Session, and DEVICE_PASSWORD_VERIFIER is taken without one (lms-dev,
        // 2026-10-02, as amazon-cognito-identity-js sends it): an empty session is left out, never sent empty.
        ...(session ? { Session: session } : {}),
        ChallengeResponses: responses,
        ...(contextData ? { UserContextData: contextData } : {}),
        ...(clientMetadata ? { ClientMetadata: clientMetadata } : {}),
      }),
    /**
     * Confirms the device Cognito issued with a sign-in (device-verifier.ts), so the sign-in's refresh token renews
     * (session-store.ts, the ordering rule). Confirming does not remember it: the pool remembers a device only when asked
     * (`DeviceOnlyRememberedOnUserPrompt`), and asking is "Remember this device" (updateDeviceStatus, gairm/lms#400).
     */
    confirmDevice: (accessToken: string, deviceKey: string, verifier: { Salt: string; PasswordVerifier: string }, deviceName: string, timeoutMs?: number) =>
      call<{ UserConfirmationNecessary?: boolean }>("ConfirmDevice", { AccessToken: accessToken, DeviceKey: deviceKey, DeviceSecretVerifierConfig: verifier, DeviceName: deviceName }, timeoutMs),
    /**
     * *Forgot your password?* on our own screen (gairm/lms#394). With `preventUserExistenceErrors` on, an address with
     * no account is answered as one with an account (a masked destination, no email; lms-dev, 2026-10-01), and a code
     * confirmed for it is `CodeMismatchException`. A reset fires the pool's Post confirmation trigger
     * (`PostConfirmation_ConfirmForgotPassword`), which stamps and signs the person out everywhere.
     */
    forgotPassword: (username: string, contextData?: UserContextData) =>
      call<{ CodeDeliveryDetails?: { Destination?: string } }>("ForgotPassword", { ClientId: clientId, Username: username, ...(contextData ? { UserContextData: contextData } : {}) }),
    confirmForgotPassword: (username: string, code: string, password: string, contextData?: UserContextData) =>
      call<Record<string, never>>("ConfirmForgotPassword", { ClientId: clientId, Username: username, ConfirmationCode: code, Password: password, ...(contextData ? { UserContextData: contextData } : {}) }),
    /** An unverified email address confirmed after sign-in (gairm/lms#394): a code to the address, then the code back. */
    sendEmailVerificationCode: (accessToken: string) => call<{ CodeDeliveryDetails?: { Destination?: string } }>("GetUserAttributeVerificationCode", { AccessToken: accessToken, AttributeName: "email" }),
    verifyEmail: (accessToken: string, code: string) => call<Record<string, never>>("VerifyUserAttribute", { AccessToken: accessToken, AttributeName: "email", Code: code }),
    forgetDevice: (accessToken: string, deviceKey: string, timeoutMs?: number) => call<Record<string, never>>("ForgetDevice", { AccessToken: accessToken, DeviceKey: deviceKey }, timeoutMs),
    /**
     * "Remember this device" (gairm/lms#400): after ConfirmDevice, `remembered`; Account › Security's Remove for the device
     * this browser signs in with, `not_remembered` — the device stays confirmed, so this session still renews (ForgetDevice
     * would end it at the next renewal: lms-dev, 2026-10-02), and the next sign-in asks for the code again.
     */
    updateDeviceStatus: (accessToken: string, deviceKey: string, status: "remembered" | "not_remembered") =>
      call<Record<string, never>>("UpdateDeviceStatus", { AccessToken: accessToken, DeviceKey: deviceKey, DeviceRememberedStatus: status }),
    /** The person's remembered devices (a confirmed device that is not remembered is not listed: lms-dev, 2026-10-01). */
    listDevices: (accessToken: string, paginationToken?: string) =>
      call<{ Devices?: DeviceRecord[]; PaginationToken?: string }>("ListDevices", { AccessToken: accessToken, Limit: 60, ...(paginationToken ? { PaginationToken: paginationToken } : {}) }),
  };
}

export type CognitoApi = ReturnType<typeof cognitoApi>;

/**
 * Cognito refused an access token because it has run out: `NotAuthorizedException`, "Access Token has expired" (lms-dev,
 * 2 Oct). A revoked token ("Access Token has been revoked", after a sign-out everywhere) is not this: renewing cannot help.
 */
export function accessTokenExpired(err: unknown): boolean {
  return err instanceof CognitoApiError && err.code === "NotAuthorizedException" && /access token has expired/i.test(err.message);
}

/**
 * What step-up's sign-in calls carry as `ClientMetadata` (R12; gairm/lms#421): the token enricher reads `starsach_purpose`
 * and writes `session.stepped_up` instead of `session.signed_in`. Not a secret and not a proof — the ceremony is.
 */
export const STEP_UP_METADATA: Readonly<Record<string, string>> = { starsach_purpose: "step_up" };

/** One of the person's devices, as ListDevices answers (epoch seconds for the dates). */
export interface DeviceRecord {
  DeviceKey: string;
  DeviceAttributes?: Array<{ Name: string; Value?: string }>;
  DeviceCreateDate?: number;
  DeviceLastModifiedDate?: number;
  DeviceLastAuthenticatedDate?: number;
}

/** Cognito's advanced-security data for one sign-in call (R8). */
export interface UserContextData {
  EncodedData: string;
}

/** What InitiateAuth and RespondToAuthChallenge answer. */
export interface AuthAnswer {
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
  /** On `SELECT_CHALLENGE`: the factors on offer (`WEB_AUTHN`, `PASSWORD`, `PASSWORD_SRP`, …). */
  AvailableChallenges?: string[];
  AuthenticationResult?: FreshTokens;
}

/** WebAuthn's binary fields travel base64url; the browser API wants ArrayBuffers and gives them back. */
export function toWebAuthnCreationOptions(options: unknown): CredentialCreationOptions {
  const o = options as { publicKey?: Record<string, unknown> } & Record<string, unknown>;
  const pk = (o.publicKey ?? o) as Record<string, unknown>;
  const exclude = (pk["excludeCredentials"] as Array<Record<string, unknown>> | undefined) ?? [];
  return {
    publicKey: {
      ...(pk as unknown as PublicKeyCredentialCreationOptions),
      challenge: fromBase64Url(String(pk["challenge"])),
      user: { ...(pk["user"] as { name: string; displayName: string }), id: fromBase64Url(String((pk["user"] as { id: string }).id)) },
      excludeCredentials: exclude.map((c) => ({ ...(c as unknown as PublicKeyCredentialDescriptor), id: fromBase64Url(String(c["id"])) })),
    },
  };
}

export function toWebAuthnRequestOptions(options: unknown): CredentialRequestOptions {
  const o = options as { publicKey?: Record<string, unknown> } & Record<string, unknown>;
  const pk = (o.publicKey ?? o) as Record<string, unknown>;
  const allow = (pk["allowCredentials"] as Array<Record<string, unknown>> | undefined) ?? [];
  return {
    publicKey: {
      ...(pk as unknown as PublicKeyCredentialRequestOptions),
      challenge: fromBase64Url(String(pk["challenge"])),
      allowCredentials: allow.map((c) => ({ ...(c as unknown as PublicKeyCredentialDescriptor), id: fromBase64Url(String(c["id"])) })),
    },
  };
}

/**
 * The passkey ceremony both sign-in and step-up run (R6: one ceremony): Cognito's `CREDENTIAL_REQUEST_OPTIONS` (JSON,
 * binary fields base64url) to the browser's prompt, and the assertion back as the JSON string `CREDENTIAL` takes.
 * Rejects as the browser does: `NotAllowedError` when the person cancels or the prompt times out.
 */
export async function passkeyAssertion(requestOptionsJson: string | undefined, signal?: AbortSignal): Promise<string> {
  const options = toWebAuthnRequestOptions(JSON.parse(requestOptionsJson ?? "{}"));
  const assertion = await navigator.credentials.get({ ...options, ...(signal ? { signal } : {}) });
  return JSON.stringify(credentialToJson(assertion));
}

/** What Cognito wants back: the credential as JSON with binary fields base64url. Modern browsers do this themselves. */
export function credentialToJson(credential: Credential | null): unknown {
  if (!credential) throw new Error("No credential was created");
  const c = credential as PublicKeyCredential & { toJSON?: () => unknown };
  if (typeof c.toJSON === "function") return c.toJSON();
  const r = c.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: toBase64Url(c.rawId),
    type: c.type,
    authenticatorAttachment: c.authenticatorAttachment,
    clientExtensionResults: c.getClientExtensionResults(),
    response: {
      clientDataJSON: toBase64Url(r.clientDataJSON),
      ...(r.attestationObject ? { attestationObject: toBase64Url(r.attestationObject), transports: r.getTransports?.() } : {}),
      ...(r.authenticatorData ? { authenticatorData: toBase64Url(r.authenticatorData), signature: toBase64Url(r.signature), userHandle: r.userHandle ? toBase64Url(r.userHandle) : null } : {}),
    },
  };
}

export function fromBase64Url(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function toBase64Url(buf: ArrayBuffer): string {
  let bin = "";
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
